import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, command, field, getObject, mutationHeaders, name, type Row } from './api/client';
import { apiError } from './api/session';
import { allRows, useAccess, useCompleteRows } from './api/data';
import { ApplicationQuestion, applicationAnswers, type Question } from './application-fields';
import { Select, Button, Empty, ErrorState, Field, Loading, useToast } from './ui';

type Section = { id: string; title: string; questions: Question[] };
export function ApplicationCreatePage() {
  const access = useAccess();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const candidates = useCompleteRows('/api/v1/candidates');
  const jobs = useCompleteRows('/api/v1/job-postings');
  const [candidateId, setCandidateId] = useState('');
  const [jobId, setJobId] = useState('');
  const [basisId, setBasisId] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [files, setFiles] = useState<Record<string, string[]>>({});
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [pending, setPending] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const job = useQuery({
    queryKey: ['application-create-job', jobId],
    enabled: Boolean(jobId),
    queryFn: ({ signal }) => getObject<Row>('/api/v1/job-postings/{id}', { id: jobId }, signal),
  });
  const bases = useQuery({
    queryKey: ['application-create-bases', candidateId],
    enabled: Boolean(candidateId),
    queryFn: ({ signal }) =>
      allRows('/api/v1/candidates/{id}/processing-basis-records', { id: candidateId }, signal),
  });
  const versions = useQuery({
    queryKey: ['application-create-form', jobId],
    enabled: Boolean(jobId),
    queryFn: ({ signal }) =>
      allRows('/api/v1/job-postings/{id}/application-form-versions', { id: jobId }, signal),
  });
  const version = versions.data?.find((row) => row.id === job.data?.applicationFormVersionId);
  const sections = (version?.sections || []) as Section[];
  const publicFiles = Boolean(
    (job.data?.channelPublications as Row[] | undefined)?.some(
      (row) => row.channel === 'PUBLIC' && row.status === 'PUBLISHED',
    ),
  );
  const missingFiles =
    !publicFiles &&
    sections.some((section) =>
      section.questions.some((question) => question.type === 'FILE' && question.required),
    );
  if (access.isLoading) return <Loading />;
  if (!access.can('candidates.manage')) return <Empty title="Недостаточно прав" />;
  const upload = async (question: Question, selected: File[]) => {
    setUploading(question.id);
    setError(null);
    try {
      const session = await api.POST(
        '/public/v1/job-postings/{publicId}/application-upload-sessions',
        {
          params: { path: { publicId: field(job.data, 'publicId') } },
          headers: mutationHeaders(),
          body: { applicationFormVersionId: version!.id, questionVersionId: question.id },
        },
      );
      if (!session.data) throw apiError(session.response, session.error);
      const grant = session.data;
      if (selected.length > grant.maxFiles)
        throw new Error(`Можно прикрепить не больше ${grant.maxFiles} файлов`);
      const ids: string[] = [];
      for (const file of selected) {
        if (file.size > grant.maxFileSizeBytes) throw new Error('Файл превышает допустимый размер');
        const body = new FormData();
        body.set('uploadSessionId', grant.uploadSessionId);
        body.set('questionVersionId', grant.questionVersionId);
        body.set('uploadGrant', grant.grantToken);
        body.set('file', file);
        const result = await api.POST('/public/v1/application-files', {
          headers: { ...mutationHeaders(), 'X-Upload-Grant': grant.grantToken },
          body: body as never,
          bodySerializer: (value) => value as unknown as FormData,
        });
        if (!result.data) throw apiError(result.response, result.error);
        ids.push(result.data.fileId);
      }
      setFiles((current) => ({ ...current, [question.id]: ids }));
      setFileNames((current) => ({ ...current, [question.id]: selected.map((file) => file.name) }));
    } catch (cause) {
      setError(cause as Error);
    } finally {
      setUploading(null);
    }
  };
  return (
    <section className="form-page">
      <h1>Новый отклик</h1>
      <form
        className="domain-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          try {
            const basis = bases.data?.find((row) => row.id === basisId);
            if (!basis) throw new Error('Выберите основание обработки данных');
            const answers = applicationAnswers(
              sections.flatMap((section) => section.questions),
              values,
              files,
            );
            const result = await command('POST', '/api/v1/applications', {
              candidateId,
              jobPostingId: jobId,
              purpose: basis.purpose,
              jurisdiction: basis.jurisdiction,
              answers,
            });
            await client.invalidateQueries();
            toast('Отклик создан');
            void navigate({
              to: '/$section/$resource/$entityId',
              params: { section: 'recruitment', resource: 'applications', entityId: result.id },
            });
          } catch (cause) {
            setError(cause as Error);
          } finally {
            setPending(false);
          }
        }}
      >
        <Field label="Кандидат">
          <Select
            name="candidateId"
            required
            value={candidateId}
            onChange={(event) => {
              setCandidateId(event.target.value);
              setBasisId('');
            }}
          >
            <option value="" disabled>
              Выберите кандидата
            </option>
            {candidates.rows.map((row) => (
              <option value={row.id} key={row.id}>
                {name(row)} · {field(row, 'primaryEmail')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вакансия">
          <Select
            name="jobPostingId"
            required
            value={jobId}
            onChange={(event) => {
              setJobId(event.target.value);
              setValues({});
              setFiles({});
              setFileNames({});
            }}
          >
            <option value="" disabled>
              Выберите вакансию
            </option>
            {jobs.rows
              .filter((row) => row.lifecycleStatus === 'OPEN' && row.applicationFormVersionId)
              .map((row) => (
                <option value={row.id} key={row.id}>
                  {field(row, 'internalTitle')}
                </option>
              ))}
          </Select>
        </Field>
        {candidateId && (
          <Field label="Основание обработки данных">
            <Select
              name="basisId"
              required
              value={basisId}
              onChange={(event) => setBasisId(event.target.value)}
            >
              <option value="" disabled>
                Выберите основание
              </option>
              {bases.data
                ?.filter((row) => row.status === 'ACTIVE')
                .map((row) => (
                  <option value={row.id} key={row.id}>
                    {field(row, 'purpose')} · {field(row, 'jurisdiction')}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        {job.isLoading || versions.isLoading || bases.isLoading ? (
          <Loading />
        ) : (
          sections.map((section) => (
            <section className="panel application-section" key={section.id}>
              <h2>{section.title}</h2>
              {section.questions.map((question) => (
                <Field label={question.label + (question.required ? ' *' : '')} key={question.id}>
                  <ApplicationQuestion
                    question={question}
                    value={values[question.id]}
                    onChange={(value) =>
                      setValues((current) => ({ ...current, [question.id]: value }))
                    }
                    onUpload={(selected) => void upload(question, selected)}
                    disabled={Boolean(uploading) || (question.type === 'FILE' && !publicFiles)}
                    fileNames={fileNames[question.id]}
                  />
                </Field>
              ))}
            </section>
          ))
        )}
        {missingFiles && (
          <p className="notice-card">
            Для этой вакансии приём откликов с обязательными файлами не настроен.
          </p>
        )}
        {[candidates.error, jobs.error, job.error, versions.error, bases.error, error]
          .filter(Boolean)
          .map((error, index) => (
            <ErrorState key={index} error={error as Error} />
          ))}
        {uploading && <p role="status">Загрузка файла…</p>}
        <footer>
          <Button
            onClick={() =>
              void navigate({
                to: '/$section/$resource',
                params: { section: 'recruitment', resource: 'applications' },
              })
            }
          >
            Отмена
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={pending || Boolean(uploading) || !version || missingFiles}
          >
            {pending ? 'Создание…' : 'Создать отклик'}
          </Button>
        </footer>
      </form>
    </section>
  );
}
